import { getPageLinkByPageType } from "@aspen/Store/Aspen/AspenLinksSelectors";
import AspenLink from "@aspen/Widgets/Basic/AspenLink";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable from "@insite/mobius/Clickable";
import isMobile from "ismobilejs";
import uniqueId from "lodash/uniqueId"
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    tagNameAs?: keyof React.ReactHTML;
    includeAccountLink?: boolean;
    fields: any;
    links: (Readonly<PageLinkModel> | {
        url: string;
        title: string;
        type?: string;
        id?: string;
        parentId?: string;
        excludeFromNavigation?: boolean;
    })[]
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currentShipToState: getCurrentShipToState(state),
    session: getSession(state),
    firstName: state.context.session.firstName,
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    currentBillToState: getCurrentBillToState(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentUserIsGuest: getCurrentUserIsGuest(state),
});
const mapDispatchToProps = {
    loadCurrentShipTo,
    signOut,
    cancelPunchOut
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AspenAccountDetailsDisplay = ({ currentShipToState, userName, firstName, session, myAccountPageLink, includeAccountLink, isPunchOutSession, fields, links, currentUserIsGuest, loadCurrentShipTo, signOut, cancelPunchOut }: Props) => {
    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };
    const handleOnClick = () => {
        const isMobileView = window.innerWidth < 1023;
        const isMobileDevice = isMobile().any;
        if (isMobileDevice || isMobileView) {
            const element = document.querySelector(".as-nav-opener");
            if (element) {
              element.dispatchEvent(new MouseEvent('click'));
            }
          }
      };
    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage" && pageLink.type !== "BudgetManagementPage");
    }
    useEffect(() => {
        if (!currentShipToState.value && !currentShipToState.isLoading && (session.billToId || session.shipToId)) {
            loadCurrentShipTo();
        }
    }, [currentShipToState, firstName, userName]);

    const renderMenuItems = (childMenuItems: PageLinkModel[] | undefined, currentDepth: number) => {
        const maxDepth = 1;
        return childMenuItems
            ?.filter(item => !item.excludeFromNavigation)
            .map((link, index) => {
                const hasChildren =
                    link.children?.some(o => !o.excludeFromNavigation) &&
                    currentDepth < maxDepth;
                return (
                    <li
                        key={`${link.title}_${index}`}
                    >
                        {link.url && (
                            <>
                                <AspenLink
                                    href={link.url} onClick={handleOnClick}
                                >
                                    {link.title}
                                </AspenLink>
                                {hasChildren
                                    ? renderMenuItems(link!.children!, currentDepth + 1)
                                    : null}
                            </>
                        )}
                    </li>
                );
            })
    };
    const renderCustomMenuItems = (childMenuItems: PageLinkModel[] | undefined, currentDepth: number) => {
        const maxDepth = 1;
        return childMenuItems
            ?.filter(item => !item.excludeFromNavigation)
            .map((link, index) => {
                const hasChildren =
                    link.children?.some(o => !o.excludeFromNavigation) &&
                    currentDepth < maxDepth;
                return (
                    <li key={`${link.title}_${index}`}>
                        {link.url && (
                            <>
                                {fields.links[index].fields.titlePrefix ? `${fields.links[index].fields.titlePrefix} ` : ''}
                                <AspenLink
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}  onClick={handleOnClick}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </AspenLink>
                                {hasChildren ? renderMenuItems(link!.children!, currentDepth + 1) : null}
                            </>
                        )}
                    </li>
                );
            })
    };
    return (
        <>
            {currentShipToState && currentShipToState.value && fields.showFirstName && <li key={uniqueId()} data-key={uniqueId()}>
                <strong className="as-user-name">{fields.firstNamePrefix} {firstName}!</strong>
            </li>}
            {fields.showHorizontalLine && (currentShipToState && currentShipToState.value && (
                (fields.showCompanyName && currentShipToState.value.companyName) ||
                (fields.showCustomerNumber && currentShipToState.value.customerNumber) ||
                (fields.showCity && currentShipToState.value.city))) && <li><hr /></li>}

            {currentShipToState && currentShipToState.value && fields.showCompanyName &&
                <li key={uniqueId()} data-key={uniqueId()}>{fields.companyNamePrefix} {currentShipToState.value.companyName}
                </li>}
            {currentShipToState && currentShipToState.value && fields.showCustomerNumber && <li key={uniqueId()} data-key={uniqueId()}>
                {fields.customerNumberPrefix} {currentShipToState.value.customerNumber}
            </li>}
            {currentShipToState && currentShipToState.value && fields.showCity &&
                <li key={uniqueId()} data-key={uniqueId()}>{fields.cityPrefix} {currentShipToState.value.city}</li>}
            {fields.showHorizontalLine && currentShipToState && currentShipToState.value && links.length > 0 && <li><hr /></li>}
            {currentShipToState && currentShipToState.value && renderCustomMenuItems(links as PageLinkModel[], 1)}
            {fields.showHorizontalLine && currentShipToState && currentShipToState.value && menuItems && menuItems.length > 0 && <li><hr /></li>}
            {currentShipToState && currentShipToState.value && renderMenuItems(menuItems, 1)}

            {fields.includeAccountLink && (<>
                {fields.showHorizontalLine && <li><hr /></li>}
                <li key={uniqueId()} data-key={uniqueId()}>
                    <a href={myAccountPageLink?.url}><strong>{translate("View Account")}</strong></a>
                </li></>
            )}

            {fields.includeSignoutLink && firstName && !currentUserIsGuest && (
                <>
                    {fields.showHorizontalLine && <li><hr /></li>}
                    <li><Clickable as="a"
                        onClick={onSignOutHandler}
                        data-test-selector="headerSignOutLink"
                        className="as-sign-out-link"
                    >
                        <strong>{isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}</strong>
                    </Clickable></li>
                </>)}
        </>
    );
}

/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(AspenAccountDetailsDisplay);